/**
 * Shopping Preferences Sync Strategy
 *
 * Backend-first synchronization strategy for shopping preferences.
 * Uses cookie-only storage with permanent/temporary flags.
 */

import { ShoppingPreferences, ShoppingPrefsPayload, SyncResult, StorageToClear } from '@lib/shopping-preferences/types';
import { storage } from '@helpers/shopping-preferences/storage';
import { createDefaultPreferences, withTimestamp } from '@helpers/shopping-preferences/defaults';
import { validatePreferences } from '@helpers/shopping-preferences/validation';
import logger from '@helpers/utils/logger/client';

/**
 * Sync preferences with backend-first priority
 *
 * For authenticated users:
 * 1. Backend is always the source of truth
 * 2. Local updates always sync to backend
 *
 * For guest users:
 * 1. Permanent cookie preferences
 * 2. Defaults
 *
 * Temporary (session) preferences are prioritized only for homepage overrides (see homepage-preferences-manager.ts)
 * and do not affect backend sync for authenticated users.
 * @param backendPrefs - Preferences from backend (authenticated users only)
 * @param isAuthenticated - Whether user is authenticated
 * @param clearTemporaryOnSync - Force clear temporary storage (e.g., on login)
 * @returns Sync result with final preferences and source
 */
export const syncPreferences = (
  backendPrefs: ShoppingPrefsPayload | null,
  isAuthenticated: boolean,
  clearTemporaryOnSync: boolean = false,
): SyncResult => {
  try {
    // Clear temporary storage if requested (e.g., user just logged in)
    if (clearTemporaryOnSync && storage.hasTemporary()) {
      logger.info('[Shopping Prefs Sync] Clearing temporary storage on login');
      storage.clear('temporary');
    }

    // Check temporary storage first (highest priority - even for authenticated users)
    // This allows navigation-based universe overrides (e.g., /womenswear/) to take precedence
    const tempPrefs = storage.getTemporary();
    const permPrefs = storage.getPermanent();
    if (tempPrefs) {
      const validation = validatePreferences(tempPrefs);
      if (validation.isValid) {
        logger.info('[Shopping Prefs Sync] Using temporary cookie preferences (session override)');
        return {
          success: true,
          source: 'cookie',
          preferences: permPrefs ?? tempPrefs,
          sessionPrefs: tempPrefs,
        };
      } else {
        logger.warn('[Shopping Prefs Sync] Invalid temporary preferences, removing', validation.errors);
        storage.clear('temporary');
      }
    }

    // Authenticated user flow - backend is source of truth (but only when no temp override)
    if (isAuthenticated && backendPrefs) {
      const backendWithTimestamp = withTimestamp(backendPrefs);
      logger.info('[Shopping Prefs Sync] Using backend as source of truth (no temp override)');
      storage.setPermanent(backendWithTimestamp);

      return {
        success: true,
        source: 'backend',
        preferences: backendWithTimestamp,
        sessionPrefs: tempPrefs ?? backendWithTimestamp,
      };
    }

    // Guest user flow - permanent cookie storage
    const cookiePrefs = storage.getPermanent();
    if (cookiePrefs) {
      const validation = validatePreferences(cookiePrefs);
      if (validation.isValid) {
        logger.info('[Shopping Prefs Sync] Using permanent cookie (guest)');
        return {
          success: true,
          source: 'cookie',
          preferences: cookiePrefs,
          sessionPrefs: tempPrefs ?? cookiePrefs,
        };
      } else {
        logger.warn('[Shopping Prefs Sync] Invalid cookie preferences, removing', validation.errors);
        storage.clear('permanent');
      }
    }

    // No preferences found, use defaults
    logger.info('[Shopping Prefs Sync] No preferences found, using defaults');
    const defaults = createDefaultPreferences();
    return {
      success: true,
      source: 'default',
      preferences: defaults,
      sessionPrefs: null,
    };
  } catch (error) {
    logger.error('[Shopping Prefs Sync] Error during sync:', error);
    return {
      success: false,
      source: null,
      preferences: createDefaultPreferences(),
      sessionPrefs: null,
      error: error as Error,
    };
  }
};

/**
 * Persist preferences to cookie storage
 *
 * @param preferences - Preferences to persist
 * @param isAuthenticated - Whether user is authenticated
 * @param isTemporary - Whether this is a temporary (session) preference
 * @returns Success status
 */
export const persistPreferences = (
  preferences: ShoppingPreferences,
  isAuthenticated: boolean,
  isTemporary: boolean = false,
): boolean => {
  try {
    const storageType = isTemporary ? 'temporary' : 'permanent';
    const success = storage.set(preferences, storageType);

    if (!success) {
      logger.error(`[Shopping Prefs Persist] Failed to save ${storageType} preferences`);
      return false;
    }

    logger.info(`[Shopping Prefs Persist] Saved ${storageType} preferences to cookie`);
    return true;
  } catch (error) {
    logger.error('[Shopping Prefs Persist] Error persisting preferences:', error);
    return false;
  }
};

/**
 * Clear preferences from storage
 *
 * @param target - Which storage to clear
 */
export const clearPersistedPreferences = (target: StorageToClear): void => {
  try {
    storage.clear(target);
    logger.info(`[Shopping Prefs Clear] Cleared ${target} preferences`);
  } catch (error) {
    logger.error('[Shopping Prefs Clear] Error clearing preferences:', error);
  }
};
